import React from 'react';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { TrendingUp, TrendingDown } from 'lucide-react';

interface MetricCardProps {
  title: string;
  value: string;
  change?: {
    amount: number;
    percentage: number;
    isPositive: boolean;
  };
  icon: React.ReactNode;
  iconColor: string;
  iconBgColor: string;
  className?: string;
}

export function MetricCard({ 
  title, 
  value, 
  change, 
  icon, 
  iconColor, 
  iconBgColor, 
  className 
}: MetricCardProps) {
  return (
    <Card className={cn(
      // Glass effect from design reference
      "group relative overflow-hidden bg-background-secondary dark:bg-background-dark-secondary backdrop-blur-xl backdrop-saturate-150 border border-border dark:border-border-dark shadow-sm hover:shadow-md transition-all duration-200 active:scale-[0.98]",
      className
    )}>
      {/* Subtle background pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-secondary/5 dark:from-primary-dark/5 dark:to-secondary-dark/5" />
      
      {/* Content */}
      <div className="relative p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2">
              {title}
            </p>
            <p className="text-3xl font-bold text-text-primary dark:text-text-dark-primary tracking-tight">
              {value}
            </p>
            
            {change && (
              <div className="flex items-center mt-3 space-x-1">
                {change.isPositive ? (
                  <TrendingUp className="w-4 h-4 text-positive dark:text-positive-dark" />
                ) : (
                  <TrendingDown className="w-4 h-4 text-negative dark:text-negative-dark" />
                )}
                <span className={cn(
                  "text-sm font-medium",
                  change.isPositive 
                    ? "text-positive dark:text-positive-dark" 
                    : "text-negative dark:text-negative-dark"
                )}>
                  {change.isPositive ? '+' : ''}{change.percentage.toFixed(1)}%
                </span>
                <span className="text-sm text-text-secondary dark:text-text-dark-secondary">vs last period</span>
              </div>
            )}
          </div>
          
          {/* Icon */}
          <div className={cn(
            "flex items-center justify-center w-12 h-12 rounded-xl transition-all duration-200 group-hover:scale-110",
            iconBgColor
          )}>
            <div className={iconColor}>
              {icon}
            </div>
          </div>
        </div>
      </div>
      
      {/* Hover effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-secondary/10 to-primary/10 dark:from-primary-dark/10 dark:to-secondary-dark/10 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
    </Card>
  );
} 